import React, { Component } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { StateSelection } from "@components/common";
import { JurisdictionService } from "@services";
import utils from "@utils";

import "./CreateJurisdictionModal.scss";

class CreateJurisdictionModal extends Component {
	constructor(props) {
		super(props);
		this.initialState = {
			jurisdictionName: "",
			jurisdictionDepartment: "",
			jurisdictionAddress: "",
			jurisdictionCity: "",
			jurisdictionCounty: "",
			jurisdictionState: "",
			jurisdictionZipCode: "",
			loading: false,
			errors: [],
		};
		this.state = { ...this.initialState };
	}

	componentDidUpdate(prevProps) {
		const { show, jurisdictionId } = this.props;
		if (show && prevProps.jurisdictionId !== jurisdictionId && jurisdictionId) {
			this.getJurisdictionById();
		}
	}

	// Service Methods
	getJurisdictionById = () => {
		const { lang } = global;
		const { jurisdictionId } = this.props;
		JurisdictionService.getById(jurisdictionId)
			.then(({ data }) => {
				this.setState({
					jurisdictionName: data.Name || "",
					jurisdictionDepartment: data.DepartmentType || "",
					jurisdictionAddress: data.StreetAddress1 || "",
					jurisdictionCity: data.City || "",
					jurisdictionCounty: data.County || "",
					jurisdictionState: data.StateId || "",
					jurisdictionZipCode: data.Zipcode || "",
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Jurisdiction Details"),
					title: "Error",
				});
			});
	};

	createJurisdiction = params => {
		const { lang } = global;
		const { onSuccess } = this.props;
		this.setState({ loading: true });
		JurisdictionService.create(params)
			.then(({ data }) => {
				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully created"),
				});
				onSuccess(data);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error creating Jurisdiction"),
					title: "Error",
				});
			})
			.finally(() => {
				this.setState({ loading: false });
			});
	};

	editJurisdiction = params => {
		const { lang } = global;
		const { onSuccess } = this.props;
		this.setState({ loading: true });
		JurisdictionService.edit(params)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully updated"),
				});
				onSuccess();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating the jurisdiction"),
					title: "Error",
				});
			})
			.finally(() => {
				this.setState({ loading: false });
			});
	};
	// Service Methods

	handleInputChange = e => {
		const {
			target: { name, value },
		} = e;
		if (name === "jurisdictionZipCode" && value.replace(/[0-9]/gi, "").length > 0) return;
		this.setState({ [name]: value });
	};

	validateForm = () => {
		const {
			jurisdictionName,
			jurisdictionDepartment,
			jurisdictionAddress,
			jurisdictionCity,
			jurisdictionCounty,
			jurisdictionState,
			jurisdictionZipCode,
		} = this.state;

		const errors = [];

		if (jurisdictionName.toString().trim() === "") errors.push("jurisdictionName");
		if (jurisdictionDepartment.toString().trim() === "") errors.push("jurisdictionDepartment");
		if (jurisdictionAddress.toString().trim() === "") errors.push("jurisdictionAddress");
		if (jurisdictionCity.toString().trim() === "") errors.push("jurisdictionCity");
		if (jurisdictionCounty.toString().trim() === "") errors.push("jurisdictionCounty");
		if (jurisdictionState.toString().trim() === "") errors.push("jurisdictionState");
		if (jurisdictionZipCode.toString().trim().length < 5) errors.push("jurisdictionZipCode");

		this.setState({ errors });
		if (errors.length > 0) return false;
		return true;
	};

	handleOnSave = () => {
		const { jurisdictionId, user } = this.props;
		const {
			jurisdictionName,
			jurisdictionDepartment,
			jurisdictionAddress,
			jurisdictionCity,
			jurisdictionCounty,
			jurisdictionState,
			jurisdictionZipCode,
		} = this.state;
		const dataParams = {
			Name: jurisdictionName,
			DepartmentType: jurisdictionDepartment,
			StreetAddress1: jurisdictionAddress,
			City: jurisdictionCity,
			County: jurisdictionCounty,
			StateId: jurisdictionState,
			Zipcode: jurisdictionZipCode,
		};
		if (!this.validateForm()) return;

		if (jurisdictionId) {
			this.editJurisdiction({ ...dataParams, Id: jurisdictionId });
		} else {
			this.createJurisdiction({ ...dataParams, IsActive: user?.isSuperAdmin() || false });
		}
	};

	onCancel = () => {
		const { onClose } = this.props;
		onClose();
		this.setState(this.initialState);
	};

	render() {
		const { lang } = global;
		const { show, jurisdictionId } = this.props;
		const {
			jurisdictionName,
			jurisdictionDepartment,
			jurisdictionAddress,
			jurisdictionCity,
			jurisdictionCounty,
			jurisdictionState,
			jurisdictionZipCode,
			loading,
			errors,
		} = this.state;

		return (
			<Modal
				id="jurisdiction-modal"
				show={show}
				onHide={this.onCancel}
			>
				<Modal.Header closeButton>
					<Modal.Title className="text-center">
						<Choose>
							<When condition={jurisdictionId}>{lang.t("Edit Jurisdiction")}</When>
							<Otherwise>{lang.t("Add Jurisdiction")}</Otherwise>
						</Choose>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionName"
							className="col-sm-3"
						>{`${lang.t("Name")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<input
								type="text"
								id="jurisdictionName"
								name="jurisdictionName"
								className={`form-control ${errors.includes("jurisdictionName") ? "has-error" : ""}`}
								value={jurisdictionName}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionDepartment"
							className="col-sm-3"
						>{`${lang.t("Department")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<input
								type="text"
								id="jurisdictionDepartment"
								name="jurisdictionDepartment"
								className={`form-control ${errors.includes("jurisdictionDepartment") ? "has-error" : ""}`}
								value={jurisdictionDepartment}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionAddress"
							className="col-sm-3"
						>{`${lang.t("Address")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<input
								type="text"
								id="jurisdictionAddress"
								name="jurisdictionAddress"
								className={`form-control ${errors.includes("jurisdictionAddress") ? "has-error" : ""}`}
								value={jurisdictionAddress}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionCity"
							className="col-sm-3"
						>{`${lang.t("City")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<input
								type="text"
								id="jurisdictionCity"
								name="jurisdictionCity"
								className={`form-control ${errors.includes("jurisdictionCity") ? "has-error" : ""}`}
								value={jurisdictionCity}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionCounty"
							className="col-sm-3"
						>{`${lang.t("County")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<input
								type="text"
								id="jurisdictionCounty"
								name="jurisdictionCounty"
								className={`form-control ${errors.includes("jurisdictionCounty") ? "has-error" : ""}`}
								value={jurisdictionCounty}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionState"
							className="col-sm-3"
						>{`${lang.t("State")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<StateSelection
								id="jurisdictionState"
								name="jurisdictionState"
								className={`form-control ${errors.includes("jurisdictionState") ? "has-error" : ""}`}
								value={jurisdictionState}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label
							htmlFor="jurisdictionZipCode"
							className="col-sm-3"
						>{`${lang.t("Zip Code")}:`}</label>
						<div className="col-xs-12 col-sm-9">
							<input
								type="tel"
								id="jurisdictionZipCode"
								name="jurisdictionZipCode"
								className={`form-control ${errors.includes("jurisdictionZipCode") ? "has-error" : ""}`}
								value={jurisdictionZipCode}
								onChange={e => this.handleInputChange(e)}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.handleOnSave();
								}}
								maxLength="5"
							/>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-link"
						onClick={this.onCancel}
					>
						{lang.t("Cancel")}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						onClick={this.handleOnSave}
						disabled={loading}
					>
						<Choose>
							<When condition={loading}>
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</When>
							<Otherwise>{lang.t("Save")}</Otherwise>
						</Choose>
					</button>
				</Modal.Footer>
			</Modal>
		);
	}
}

CreateJurisdictionModal.propTypes = {
	user: PropTypes.object,
	show: PropTypes.bool,
	jurisdictionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	onSuccess: PropTypes.func,
	onClose: PropTypes.func,
};

CreateJurisdictionModal.defaultProps = {
	user: {
		isSuperAdmin: () => false,
	},
	show: false,
	jurisdictionId: undefined,
	onSuccess: () => {},
	onClose: () => {},
};

export default CreateJurisdictionModal;
